/**
 * Notification Scheduler Service
 * Platform-agnostic service for scheduling challenge reminders and leaderboard updates
 */

import { ChallengeService, LeaderboardService, FirebaseService } from '../index';
import { Challenge, ChallengeParticipation, LeaderboardEntry, User, ApiResponse } from '../types';

// Import the tracking service interface (will be implemented by platform-specific code)
export interface INotificationTrackingService {
    recordNotificationSent(notification: {
        type: 'challenge_reminder' | 'leaderboard_update' | 'achievement_celebration' | 'daily_progress';
        title: string;
        body: string;
        data?: any;
    }): Promise<void>;
    wasNotificationSentToday(type: 'challenge_reminder' | 'leaderboard_update' | 'achievement_celebration' | 'daily_progress'): Promise<boolean>;
    wereChallengeRemindersSentToday(): Promise<boolean>;
}

export interface NotificationContent {
    title: string;
    body: string;
    data?: Record<string, any>;
    priority?: 'low' | 'normal' | 'high';
}

export interface ChallengeReminder {
    challengeId: string;
    challengeTitle: string;
    currentProgress: number;
    targetValue: number;
    progressPercentage: number;
    daysRemaining: number;
    lastUpdated: Date | null;
}

export interface LeaderboardUpdate {
    challengeId: string;
    challengeTitle: string;
    currentPosition: number;
    totalParticipants: number;
    progressToNextPosition: number;
    pointsEarned: number;
}

export interface NotificationSchedule {
    enabled: boolean;
    frequency: 'daily' | 'twice_daily' | 'custom';
    times: string[]; // HH:mm format
    challengeReminders: boolean;
    leaderboardUpdates: boolean;
    achievementCelebrations: boolean;
    dailyProgress: boolean;
    quietHours: {
        enabled: boolean;
        start: string; // HH:mm
        end: string;   // HH:mm
    };
}

export interface NotificationStats {
    totalScheduled: number;
    totalSent: number;
    totalDismissed: number;
    lastReminderSent: Date | null;
    nextReminderScheduled: Date | null;
}

export class NotificationScheduler {
    private challengeService: ChallengeService;
    private leaderboardService: LeaderboardService;
    private firebaseService: FirebaseService;
    private trackingService: INotificationTrackingService | null;
    private schedule: NotificationSchedule;
    private stats: NotificationStats;
    private _isActive = false;
    private reminderIntervals: Map<string, any> = new Map();

    constructor(
        challengeService: ChallengeService,
        leaderboardService: LeaderboardService,
        firebaseService: FirebaseService,
        schedule: Partial<NotificationSchedule> = {},
        trackingService?: INotificationTrackingService
    ) {
        this.challengeService = challengeService;
        this.leaderboardService = leaderboardService;
        this.firebaseService = firebaseService;
        this.trackingService = trackingService || null;

        this.schedule = {
            enabled: true,
            frequency: 'twice_daily',
            times: ['09:00', '18:00'],
            challengeReminders: true,
            leaderboardUpdates: true,
            achievementCelebrations: true,
            dailyProgress: true,
            quietHours: {
                enabled: false,
                start: '22:00',
                end: '08:00'
            },
            ...schedule
        };

        this.stats = {
            totalScheduled: 0,
            totalSent: 0,
            totalDismissed: 0,
            lastReminderSent: null,
            nextReminderScheduled: null
        };
    }

    /**
     * Start the notification scheduler
     */
    async start(): Promise<ApiResponse<void>> {
        try {
            if (this._isActive) {
                return { success: true };
            }

            console.log('🔔 NotificationScheduler: Starting notification scheduler...');

            if (!this.schedule.enabled) {
                console.log('🔔 NotificationScheduler: Notifications are disabled');
                return { success: true };
            }

            // Schedule initial reminders
            await this.scheduleReminders();

            this._isActive = true;
            console.log('✅ NotificationScheduler: Started successfully');
            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ NotificationScheduler: Failed to start:', errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Stop the notification scheduler
     */
    async stop(): Promise<ApiResponse<void>> {
        try {
            if (!this._isActive) {
                return { success: true };
            }

            console.log('🔔 NotificationScheduler: Stopping notification scheduler...');

            // Clear all scheduled reminders
            this.clearAllReminders();

            this._isActive = false;
            console.log('✅ NotificationScheduler: Stopped successfully');
            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ NotificationScheduler: Failed to stop:', errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Schedule challenge reminders based on configuration
     */
    private async scheduleReminders(): Promise<void> {
        try {
            console.log('🔔 NotificationScheduler: Scheduling reminders...');

            // Clear existing reminders
            this.clearAllReminders();

            if (this.schedule.frequency === 'daily') {
                await this.scheduleDailyReminders();
            } else if (this.schedule.frequency === 'twice_daily') {
                await this.scheduleTwiceDailyReminders();
            } else if (this.schedule.frequency === 'custom') {
                await this.scheduleCustomReminders();
            }

            console.log('✅ NotificationScheduler: Reminders scheduled successfully');

        } catch (error) {
            console.error('❌ NotificationScheduler: Failed to schedule reminders:', error);
        }
    }

    /**
     * Schedule daily reminders
     */
    private async scheduleDailyReminders(): Promise<void> {
        const reminderTime = this.schedule.times[0] || '18:00';
        const [hours, minutes] = reminderTime.split(':').map(Number);

        const now = new Date();
        const reminderDate = new Date(now);
        reminderDate.setHours(hours, minutes, 0, 0);

        // If time has passed today, schedule for tomorrow
        if (reminderDate <= now) {
            reminderDate.setDate(reminderDate.getDate() + 1);
        }

        const delay = reminderDate.getTime() - now.getTime();
        const intervalId: ReturnType<typeof setInterval> = setInterval(async () => {
            await this.sendDailyReminders();
            // Schedule next day
            setTimeout(() => this.scheduleDailyReminders(), 24 * 60 * 60 * 1000);
        }, delay);

        this.reminderIntervals.set('daily', intervalId);
        this.stats.nextReminderScheduled = reminderDate;

        console.log(`🔔 NotificationScheduler: Daily reminder scheduled for ${reminderDate.toLocaleString()}`);
    }

    /**
     * Schedule twice daily reminders
     */
    private async scheduleTwiceDailyReminders(): Promise<void> {
        const morningTime = this.schedule.times[0] || '09:00';
        const eveningTime = this.schedule.times[1] || '18:00';

        // Schedule morning reminder
        await this.scheduleTimeBasedReminder(morningTime, 'morning');

        // Schedule evening reminder
        await this.scheduleTimeBasedReminder(eveningTime, 'evening');
    }

    /**
     * Schedule custom reminders
     */
    private async scheduleCustomReminders(): Promise<void> {
        for (let i = 0; i < this.schedule.times.length; i++) {
            const time = this.schedule.times[i];
            await this.scheduleTimeBasedReminder(time, `custom_${i}`);
        }
    }

    /**
     * Schedule a reminder for a specific time
     */
    private async scheduleTimeBasedReminder(time: string, type: string): Promise<void> {
        const [hours, minutes] = time.split(':').map(Number);

        const now = new Date();
        const reminderDate = new Date(now);
        reminderDate.setHours(hours, minutes, 0, 0);

        // If time has passed today, schedule for tomorrow
        if (reminderDate <= now) {
            reminderDate.setDate(reminderDate.getDate() + 1);
        }

        const delay = reminderDate.getTime() - now.getTime();
        const intervalId: ReturnType<typeof setInterval> = setInterval(async () => {
            await this.sendReminderByType(type);
            // Schedule next day
            setTimeout(() => this.scheduleTimeBasedReminder(time, type), 24 * 60 * 60 * 1000);
        }, delay);

        this.reminderIntervals.set(type, intervalId);

        console.log(`🔔 NotificationScheduler: ${type} reminder scheduled for ${reminderDate.toLocaleString()}`);
    }

    /**
     * Send daily reminders
     */
    private async sendDailyReminders(): Promise<void> {
        try {
            console.log('🔔 NotificationScheduler: Sending daily reminders...');

            // This will be implemented by platform-specific code
            // For now, we'll just log the action
            console.log('🔔 NotificationScheduler: Daily reminder triggered - platform should handle notification');

            // Record the notification if tracking service is available
            if (this.trackingService) {
                await this.trackingService.recordNotificationSent({
                    type: 'challenge_reminder',
                    title: 'Daily Challenge Reminder',
                    body: 'Time to check your challenge progress!',
                    data: { source: 'daily_reminder' }
                });
            }

            this.stats.lastReminderSent = new Date();
            this.stats.totalSent++;

        } catch (error) {
            console.error('❌ NotificationScheduler: Failed to send daily reminders:', error);
        }
    }

    /**
     * Send reminder based on type
     */
    private async sendReminderByType(type: string): Promise<void> {
        try {
            console.log(`🔔 NotificationScheduler: Sending ${type} reminder...`);

            // This will be implemented by platform-specific code
            console.log(`🔔 NotificationScheduler: ${type} reminder triggered - platform should handle notification`);

            // Record the notification if tracking service is available
            if (this.trackingService) {
                const notificationType = type === 'morning' || type === 'evening' ? 'challenge_reminder' : 'challenge_reminder';
                await this.trackingService.recordNotificationSent({
                    type: notificationType,
                    title: `${type.charAt(0).toUpperCase() + type.slice(1)} Challenge Reminder`,
                    body: 'Time to check your challenge progress!',
                    data: { source: `${type}_reminder` }
                });
            }

            this.stats.lastReminderSent = new Date();
            this.stats.totalSent++;

        } catch (error) {
            console.error(`❌ NotificationScheduler: Failed to send ${type} reminder:`, error);
        }
    }

    /**
     * Generate challenge reminder content
     */
    async generateChallengeReminder(userId: string): Promise<NotificationContent[]> {
        try {
            // Check if challenge reminders were already sent today
            if (this.trackingService) {
                const alreadySent = await this.trackingService.wereChallengeRemindersSentToday();
                if (alreadySent) {
                    console.log('🔔 NotificationScheduler: Challenge reminders already sent today, skipping');
                    return [];
                }
            }

            const activeChallenges = await this.challengeService.getActiveChallenges(userId);

            if (!activeChallenges || activeChallenges.length === 0) {
                return [];
            }

            const reminders: NotificationContent[] = [];

            for (const challenge of activeChallenges) {
                try {
                    // Get participation data directly from Firebase service
                    const participation = await this.firebaseService.getParticipation(userId, challenge.id);

                    if (!participation) {
                        continue;
                    }

                    // Helper functions to get target value and unit from new or legacy fields
                    const getTargetValue = (challenge: any) => challenge.measurementTarget || challenge.targetValue;
                    const getTargetUnit = (challenge: any) => challenge.measurementUnit || challenge.targetUnit;

                    // Calculate progress percentage
                    const targetValue = getTargetValue(challenge);
                    const progressPercentage = Math.round((participation.currentValue / targetValue) * 100);
                    const daysRemaining = this.calculateDaysRemaining(challenge.endDate);

                    // Generate reminder content based on progress
                    const reminder = this.createChallengeReminderContent(challenge, participation, progressPercentage, daysRemaining);
                    reminders.push(reminder);
                } catch (error) {
                    console.warn(`⚠️ NotificationScheduler: Failed to get progress for challenge ${challenge.id}:`, error);
                    continue;
                }
            }

            return reminders;

        } catch (error) {
            console.error('❌ NotificationScheduler: Failed to generate challenge reminders:', error);
            return [];
        }
    }

    /**
     * Generate leaderboard update content
     */
    async generateLeaderboardUpdate(userId: string, challengeId: string): Promise<NotificationContent | null> {
        try {
            // Use getLeaderboardEntries instead of getLeaderboard
            const leaderboardEntries = await this.leaderboardService.getLeaderboardEntries(challengeId, 'global');

            if (!leaderboardEntries || leaderboardEntries.length === 0) {
                return null;
            }

            // Find user's position
            const userEntry = leaderboardEntries.find((entry: LeaderboardEntry) => entry.userId === userId);
            if (!userEntry) {
                return null;
            }

            // Get challenge data from the active challenges list instead of a separate call
            const activeChallenges = await this.challengeService.getActiveChallenges(userId);
            const challenge = activeChallenges.find(c => c.id === challengeId);

            if (!challenge) {
                return null;
            }

            return this.createLeaderboardUpdateContent(challenge, userEntry, leaderboardEntries.length);

        } catch (error) {
            console.error('❌ NotificationScheduler: Failed to generate leaderboard update:', error);
            return null;
        }
    }

    /**
     * Create challenge reminder notification content
     */
    private createChallengeReminderContent(
        challenge: Challenge,
        progress: ChallengeParticipation,
        progressPercentage: number,
        daysRemaining: number
    ): NotificationContent {
        let title: string;
        let body: string;

        if (progressPercentage >= 100) {
            title = '🎉 Challenge Completed!';
            body = `Congratulations! You've completed "${challenge.title}"!`;
        } else if (progressPercentage >= 80) {
            title = '🔥 Almost There!';
            body = `You're ${progressPercentage}% done with "${challenge.title}" - just ${daysRemaining} days left!`;
        } else if (progressPercentage >= 50) {
            title = '💪 Halfway There!';
            body = `Great progress! You're ${progressPercentage}% through "${challenge.title}" with ${daysRemaining} days remaining.`;
        } else if (daysRemaining <= 3) {
            title = '⏰ Time is Running Out!';
            body = `Only ${daysRemaining} days left for "${challenge.title}" - you're ${progressPercentage}% complete!`;
        } else {
            title = '📱 Challenge Update';
            body = `Don't forget to update your progress for "${challenge.title}" - you're ${progressPercentage}% complete!`;
        }

        return {
            title,
            body,
            data: {
                type: 'challenge_reminder',
                challengeId: challenge.id,
                progressPercentage,
                daysRemaining
            },
            priority: progressPercentage >= 80 ? 'high' : 'normal'
        };
    }

    /**
     * Create leaderboard update notification content
     */
    private createLeaderboardUpdateContent(
        challenge: Challenge,
        userEntry: LeaderboardEntry,
        totalParticipants: number
    ): NotificationContent {
        const position = userEntry.rank;
        const progress = userEntry.progress || 0;
        const progressPercentage = Math.round(progress * 100);

        let title: string;
        let body: string;

        if (position === 1) {
            title = '🥇 You\'re in the Lead!';
            body = `You're #1 in "${challenge.title}" with ${progressPercentage}% progress! Keep it up!`;
        } else if (position <= 3) {
            title = '🏆 Top 3 Position!';
            body = `You're #${position} in "${challenge.title}" - great job staying in the top tier!`;
        } else if (position <= 10) {
            title = '🔥 Top 10!';
            body = `You're #${position} in "${challenge.title}" - excellent progress!`;
        } else {
            title = '📊 Leaderboard Update';
            body = `You're #${position} of ${totalParticipants} in "${challenge.title}" - keep pushing forward!`;
        }

        return {
            title,
            body,
            data: {
                type: 'leaderboard_update',
                challengeId: challenge.id,
                position,
                totalParticipants,
                progressPercentage
            },
            priority: position <= 3 ? 'high' : 'normal'
        };
    }

    /**
     * Calculate days remaining until challenge end
     */
    private calculateDaysRemaining(endDate: string): number {
        const end = new Date(endDate);
        const now = new Date();
        const diffTime = end.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    }

    /**
     * Check if current time is within quiet hours
     */
    private isQuietHours(): boolean {
        if (!this.schedule.quietHours.enabled) {
            return false;
        }

        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();

        const [startHour, startMinute] = this.schedule.quietHours.start.split(':').map(Number);
        const [endHour, endMinute] = this.schedule.quietHours.end.split(':').map(Number);

        const startTime = startHour * 60 + startMinute;
        const endTime = endHour * 60 + endMinute;

        if (startTime <= endTime) {
            // Same day range (e.g., 08:00 to 22:00)
            return currentTime >= startTime && currentTime <= endTime;
        } else {
            // Overnight range (e.g., 22:00 to 08:00)
            return currentTime >= startTime || currentTime <= endTime;
        }
    }

    /**
     * Clear all scheduled reminders
     */
    private clearAllReminders(): void {
        this.reminderIntervals.forEach((interval, key) => {
            clearInterval(interval);
            console.log(`🔔 NotificationScheduler: Cleared ${key} reminder`);
        });
        this.reminderIntervals.clear();
    }

    /**
     * Update notification schedule
     */
    updateSchedule(newSchedule: Partial<NotificationSchedule>): void {
        this.schedule = { ...this.schedule, ...newSchedule };
        console.log('🔔 NotificationScheduler: Schedule updated:', this.schedule);

        // Restart scheduler if active
        if (this._isActive) {
            this.stop().then(() => this.start());
        }
    }

    /**
     * Get current schedule
     */
    getSchedule(): NotificationSchedule {
        return { ...this.schedule };
    }

    /**
     * Get notification statistics
     */
    getStats(): NotificationStats {
        return { ...this.stats };
    }

    /**
     * Check if scheduler is active
     */
    isActive(): boolean {
        return this._isActive;
    }
}
